import React, { useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
    Typography,
    Divider,
    Card,
    CardContent,
    useTheme,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    IconButton,
    SelectChangeEvent,
} from '@mui/material'
import { DataGridPro, gridClasses, GridColDef } from '@mui/x-data-grid-pro'
import EditIcon from '@mui/icons-material/Edit'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'

import { InstrumentApi } from '../../../api/InstrumentApi'
import { CustomDataGridToolbar } from '../../../components/custom-grid-components/CustomDataGridToolbar'

import { InstrumentEntity } from 'types/InstrumentEntity'

const gridTextI18n = (locale: string) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga avnoterade instrument hittades</Typography>
        </Box>
    )
}

const getColumns = (
    updateDelistingReason: (id: string, reason: string) => Promise<void>,
    editingDelistingReasonId: string | null,
    setEditingDelistingReasonId: React.Dispatch<React.SetStateAction<string | null>>,
    t: TFunction,
) => {
    const cols: Array<GridColDef<InstrumentEntity>> = [
        {
            field: 'name',
            headerName: 'Namn',
            flex: 1,
            renderCell: (params) => (
                <Box
                    sx={{
                        display: 'flex',
                        alignContent: 'center',
                    }}
                >
                    <Typography>{params.value}</Typography>
                </Box>
            ),
        },
        {
            field: 'delistedDate',
            headerName: 'Avnoteringsdatum',
            flex: 1,
            renderCell: (params) => <Typography>{(params.value as string).slice(0, 10)}</Typography>,
        },
        {
            field: 'delistedReason',
            headerName: 'Anledning',
            flex: 1,
            renderCell: (params) => {
                if (params.row.id === editingDelistingReasonId) {
                    return (
                        <FormControl fullWidth size='small'>
                            <InputLabel id='select-label'>Välj anledning</InputLabel>
                            <Select
                                labelId='select-label'
                                id='select'
                                label='Välj anledning'
                                onChange={(event: SelectChangeEvent) =>
                                    void updateDelistingReason(params.row.id, event.target.value)
                                }
                            >
                                <MenuItem value='bankruptcy'>Konkurs</MenuItem>
                                <MenuItem value='acquisition'>Uppköp</MenuItem>
                                <MenuItem value='lack_of_compliance'>Ej uppfyllda noteringskrav</MenuItem>
                                <MenuItem value='changed_marketplace'>Byte av marknadsplats</MenuItem>
                                <MenuItem value='voluntary_delisting'>Frivillig avnotering</MenuItem>
                                <MenuItem value='merger'>Fusion</MenuItem>
                                <MenuItem value='redemption'>Inlösen</MenuItem>
                                <MenuItem value='liquidation'>Likvidation</MenuItem>
                            </Select>
                        </FormControl>
                    )
                }
                return (
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {params.value !== null ? (
                            <>
                                <Typography>{t(`site.instruments.${params.value}`)}</Typography>
                                <IconButton
                                    onClick={() => setEditingDelistingReasonId(params.row.id)}
                                    disableRipple
                                    disableFocusRipple
                                    disableTouchRipple
                                    sx={{ padding: 0 }}
                                >
                                    <EditIcon />
                                </IconButton>
                            </>
                        ) : (
                            <Typography onClick={() => setEditingDelistingReasonId(params.row.id)}>
                                Klicka för att välja anledning
                            </Typography>
                        )}
                        {/*                          */}
                    </Stack>
                )
            },
        },
    ]

    return cols
}

const instrumentApi = new InstrumentApi()

const Delistings = () => {
    const [delistedInstruments, setDelistedInstruments] = useState<InstrumentEntity[]>([])
    const [editingDelistingReasonId, setEditingDelistingReasonId] = useState<string | null>(null)
    const theme = useTheme()
    const { i18n, t } = useTranslation()
    const locale = i18n.language

    useEffect(() => {
        async function fetchMCIssues() {
            const delistedInstruments = await instrumentApi.getDelistedInstruments()
            setDelistedInstruments(delistedInstruments)
        }
        void fetchMCIssues()
    }, [])

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const updateDelistingReason = async (id: string, reason: string) => {
        const instrumentUpdated = await instrumentApi.update({ id, delistedReason: reason })

        if (instrumentUpdated) {
            const updatedDelistedInstruments = delistedInstruments.map((instrument) =>
                instrument.id === id ? { ...instrument, delistedReason: instrumentUpdated.delistedReason } : instrument,
            )
            setDelistedInstruments(updatedDelistedInstruments)
        }
        setEditingDelistingReasonId(null)
    }

    return (
        <>
            <Helmet title='Avnoteringar' />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                Avnoteringar
            </Typography>
            <Divider sx={{ my: 6 }} />
            <Card sx={{ mb: 6 }}>
                <CardContent>
                    {/* adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator */}
                    <DataGridPro
                        sx={{
                            ...disableSelectedSellOutlineStyle,
                            width: '100%',
                            '& .even': {
                                backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                            },
                            '& .odd': {
                                // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                            },
                            '& .MuiDataGrid-cell': {
                                paddingY: 4,
                                alignContent: 'center',
                            },
                        }}
                        localeText={gridTextI18n(locale)}
                        pagination
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: 'delistedReason', sort: 'asc' },
                                    { field: 'delistedDate', sort: 'desc' },
                                ],
                            },
                        }}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        rows={delistedInstruments}
                        autoHeight
                        getRowHeight={() => 'auto'}
                        columns={getColumns(
                            updateDelistingReason,
                            editingDelistingReasonId,
                            setEditingDelistingReasonId,
                            t,
                        )}
                        density='standard'
                        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                        slots={{ noResultsOverlay: CustomNoResultsOverlay, toolbar: CustomDataGridToolbar }}
                        //filterModel={filterModel}
                        // Capture changes to the filter model
                        //onFilterModelChange={handleFilterModelChange}
                        showToolbar
                    />
                </CardContent>
            </Card>
        </>
    )
}

export default Delistings
